/**
 * Exercise Selection Modal
 * Modal for searching and selecting exercises to add to a day
 */

import { Loader2, Search } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useExerciseTemplates } from '../../api/hooks/useExerciseTemplates'
import { useWorkoutData } from '../../api/hooks'
import { fuzzyFilterAndSort } from '../../lib/fuzzy-search'
import { useSplitConfigStore } from '../../stores/splitConfigStore'
import { useSplitUIStore } from '../../stores/splitUIStore'
import { useUIStore } from '../../stores/uiStore'
import type { UnifiedExercise, WorkoutSet } from '../../types'
import { Modal, ModalContent, ModalDescription, ModalHeader, ModalTitle } from '../ui/modal'
import { Input } from '../ui/input'

export const ExerciseSelectionModal = () => {
  const { addExerciseWithLastWorkoutData } = useSplitConfigStore()
  const {
    isExerciseModalOpen,
    selectedDay,
    selectedRoutineId,
    closeExerciseModal
  } = useSplitUIStore()
  const { apiOnlyExercises, isLoading, error, hasApiData, hevyCount } = useExerciseTemplates()
  const { workouts } = useWorkoutData()
  const { setActiveModal } = useUIStore()
  const [searchTerm, setSearchTerm] = useState('')

  // Filter exercises based on search term
  const filteredExercises = useMemo(() => {
    return fuzzyFilterAndSort(apiOnlyExercises, searchTerm, (exercise) => exercise.name)
  }, [apiOnlyExercises, searchTerm])

  // Find last workout data for an exercise
  const findLastWorkoutSets = useCallback((exerciseId: string): WorkoutSet[] => {
    if (!workouts) return []
    
    // Workouts are already sorted by start_time desc (most recent first)
    for (const workout of workouts) {
      const exercise = workout.exercises.find(
        (ex) => ex.exercise_template_id === exerciseId
      )
      
      if (exercise) {
        // Filter out empty sets
        return exercise.sets.filter((set) => set.type !== 'empty')
      }
    }
    
    return []
  }, [workouts])

  // Add exercise to routine with last workout data
  const handleAddExercise = (exercise: UnifiedExercise) => {
    if (!selectedRoutineId) return

    // Get last workout sets for this exercise
    const lastWorkoutSets = findLastWorkoutSets(exercise.id)
    
    // Convert WorkoutSet to ExerciseSet format
    const convertedSets = lastWorkoutSets.map((set, i) => ({
      id: `${exercise.id}-set-${i}-${Date.now()}`,
      reps: set.reps || 0,
      weight: set.weight_kg || null,
      type: 'normal' as const
    }))

    const exerciseData = {
      id: exercise.id,
      name: exercise.name,
      primaryMover: exercise.primaryMover,
      secondaryMovers: exercise.secondaryMovers,
      isCustom: exercise.isCustom
    }

    addExerciseWithLastWorkoutData(selectedRoutineId, exerciseData, convertedSets)
    closeExerciseModal()
    setSearchTerm('') // Reset search
  }

  // Reset search when modal closes
  const handleClose = () => {
    setSearchTerm('')
    closeExerciseModal()
  }

  // Update modal context when modal opens
  useEffect(() => {
    if (isExerciseModalOpen) {
      setActiveModal('exerciseSelection', {
        selectedDay,
        selectedRoutineId,
        availableExercises: filteredExercises.length,
        searchTerm
      })
    }
    
    return () => {
      if (isExerciseModalOpen) {
        setActiveModal(null, null)
      }
    }
  }, [isExerciseModalOpen, selectedDay, selectedRoutineId, filteredExercises.length, searchTerm, setActiveModal])

  return (
    <Modal open={isExerciseModalOpen} onOpenChange={(open) => !open && handleClose()}>
      <ModalContent className="flex max-h-[80vh] max-w-2xl flex-col">
        <ModalHeader>
          <ModalTitle>
            Add Exercise to{' '}
            {selectedDay ? selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1) : ''}
          </ModalTitle>
          <ModalDescription>
            Search and select exercises from your Hevy account.
            {hasApiData && (
              <span className="mt-1 block text-xs">{hevyCount} exercises loaded from Hevy</span>
            )}
          </ModalDescription>
        </ModalHeader>

        {/* Search Input */}
        <div className="relative">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            placeholder="Search exercises..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            autoFocus
          />
        </div>

        {/* Exercise List */}
        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2">Loading exercises...</span>
            </div>
          ) : error ? (
            <div className="text-destructive py-8 text-center">
              <p>Failed to load exercises from Hevy API</p>
              <p className="text-muted-foreground mt-1 text-sm">
                Please check your API key configuration
              </p>
            </div>
          ) : apiOnlyExercises.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-muted-foreground">No exercises available</p>
              <p className="text-muted-foreground mt-1 text-sm">
                Please configure your Hevy API key to load exercises
              </p>
            </div>
          ) : (
            <div className="max-h-96 space-y-2 overflow-y-auto">
              {filteredExercises.length === 0 ? (
                <div className="text-muted-foreground py-8 text-center">
                  <p>No exercises found</p>
                  {searchTerm && <p className="mt-1 text-sm">Try a different search term</p>}
                </div>
              ) : (
                filteredExercises.slice(0, 50).map(
                  (
                    exercise // Limit to 50 for performance
                  ) => (
                    <div
                      key={exercise.id}
                      onClick={() => handleAddExercise(exercise)}
                      className="hover:bg-muted/50 flex items-center rounded-lg border p-3 transition-colors cursor-pointer"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{exercise.name}</div>
                        <div className="text-muted-foreground text-sm">
                          Primary: {exercise.primaryMover}
                          {exercise.secondaryMovers.length > 0 && (
                            <span> • Secondary: {exercise.secondaryMovers.join(', ')}</span>
                          )}
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {exercise.isCustom ? 'Custom exercise' : 'Hevy exercise'}
                        </div>
                      </div>
                    </div>
                  )
                )
              )}
              {filteredExercises.length > 50 && (
                <div className="text-muted-foreground py-2 text-center text-sm">
                  Showing first 50 results. Refine search for more specific results.
                </div>
              )}
            </div>
          )}
        </div>
      </ModalContent>
    </Modal>
  )
}
